import { Classes, Colors } from "@blueprintjs/core";
import { FGCustomPanel, FGTextInput, FlexFieldGroup } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";

import {
  CardFieldGroup,
  CustomFormGenerator,
  LinkButton,
} from "../../components";
import UserIcon from "../../components/UserIcon";
import { useCommonHooks } from "../../hooks";
import { ETLCodes } from "../../locales";
import HomeTeam from "../home/HomeTeam";

const Container = styled.div`
  padding: 1.5rem 2.5rem;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ManagerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  border-right: solid 1px ${Colors.LIGHT_GRAY4};
`;

const TeamContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const NameSpan = styled.span`
  font-size: 32px;
  line-height: 40px;
  font-weight: 400;
`;

const NameContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 1rem;
`;

const ManagerNameSpan = styled.span`
  font-size: 20px;
  line-height: 40px;
  font-weight: 400;
  margin-left: 1rem;
`;

export const ProfileTab: React.FunctionComponent = () => {
  const { t, apis, user } = useCommonHooks();

  const { data, isFetching } = useQuery(
    ["user-azure-profile", user?.userId],
    async () => {
      return await apis.user.azureProfile();
    }
  );

  const onClickMail = React.useCallback((e, email) => {
    e.preventDefault();
    e.stopPropagation();
    const el = document.createElement("a");
    el.setAttribute("href", `mailto:${email}`);
    el.click();
  }, []);

  const onClickGsm = React.useCallback((e, gsm) => {
    e.preventDefault();
    e.stopPropagation();
    const el = document.createElement("a");
    el.setAttribute("href", `tel:${gsm}`);
    el.click();
  }, []);

  return (
    <Container>
      <CustomFormGenerator
        initialValues={data}
        loading={isFetching}
        editable={false}
        editMode={false}
        deleteButtonPosition="none"
        saveButtonPosition="none"
        large
      >
        <FGCustomPanel>
          {(ctx) => (
            <TitleContainer>
              <div
                className={isFetching ? Classes.SKELETON : ""}
                style={{ borderRadius: "50%" }}
              >
                <div style={{ display: "flex", justifyContent: "center" }}>
                  {ctx.formik.values?.photo ? (
                    <img
                      src={`data:image/png;base64,${ctx.formik.values.photo}`}
                      alt="Profil"
                      style={{ maxHeight: "100px", borderRadius: "50%" }}
                    />
                  ) : (
                    <UserIcon fontSize={48} size={100} />
                  )}
                </div>
              </div>
              <NameContainer>
                <NameSpan>{ctx.formik.values?.displayName}</NameSpan>
                {!!ctx.formik.values?.jobTitle && (
                  <span className={Classes.TEXT_MUTED}>
                    {ctx.formik.values?.jobTitle}
                    {!!ctx.formik.values?.department &&
                      ` - ${ctx.formik.values?.department}`}
                  </span>
                )}
              </NameContainer>
            </TitleContainer>
          )}
        </FGCustomPanel>
        <FlexFieldGroup>
          <CardFieldGroup columns={3} title={t(ETLCodes.ContactInformation)}>
            <FGTextInput
              name="mail"
              label={t(ETLCodes.Email)}
              renderReadOnlyValue={(value) => (
                <LinkButton
                  text={value}
                  onClick={(e) => onClickMail(e, value)}
                />
              )}
            />
            <FGTextInput
              name="phone"
              label={t(ETLCodes.TelephonePro)}
              renderReadOnlyValue={(value) => (
                <LinkButton
                  text={value}
                  onClick={(e) => onClickGsm(e, value)}
                />
              )}
            />
            <FGTextInput
              name="mobile"
              label={t(ETLCodes.Mobile)}
              renderReadOnlyValue={(value) => (
                <LinkButton
                  text={value}
                  onClick={(e) => onClickGsm(e, value)}
                />
              )}
            />
            <FGTextInput
              name="officeLocation"
              label={t(ETLCodes.LieuDeTravail)}
              renderReadOnlyValue={(v) => v}
            />
            <FGTextInput
              name="department"
              label={t(ETLCodes.Service)}
              renderReadOnlyValue={(v) => v}
            />
            <FGTextInput
              name="jobTitle"
              label={t(ETLCodes.JobTitle)}
              renderReadOnlyValue={(v) => v}
            />
          </CardFieldGroup>
          <FGCustomPanel>
            {(ctx) =>
              !!ctx.formik.values?.manager && (
                <CardFieldGroup
                  title={t(ETLCodes.Organization)}
                  columns={[3, 9]}
                >
                  <ManagerContainer>
                    <span
                      className={Classes.TEXT_MUTED}
                      style={{ alignSelf: "flex-start" }}
                    >
                      {t(ETLCodes.Manager)}
                    </span>
                    <div
                      className={isFetching ? Classes.SKELETON : ""}
                      style={{
                        borderRadius: "50%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flex: 1,
                        }}
                      >
                        {ctx.formik.values?.manager?.photo ? (
                          <img
                            src={`data:image/png;base64,${ctx.formik.values.photo}`}
                            alt="Profil"
                            style={{ maxHeight: "50px", borderRadius: "50%" }}
                          />
                        ) : (
                          <UserIcon
                            fontSize={20}
                            size={50}
                            initials={
                              ctx.formik.values?.manager?.displayName?.[0] +
                              ctx.formik.values?.manager?.displayName?.split(
                                " "
                              )?.[1]?.[0]
                            }
                          />
                        )}
                      </div>
                    </div>
                    <ManagerNameSpan>
                      {ctx.formik.values?.manager?.displayName}
                    </ManagerNameSpan>
                  </ManagerContainer>
                  <TeamContainer>
                    <span className={Classes.TEXT_MUTED}>
                      {t(ETLCodes.Team)}
                    </span>
                    <HomeTeam profileMode />
                  </TeamContainer>
                </CardFieldGroup>
              )
            }
          </FGCustomPanel>
        </FlexFieldGroup>
      </CustomFormGenerator>
    </Container>
  );
};

export default ProfileTab;
